import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Paper, TextField, Button, Typography, ToggleButtonGroup, ToggleButton, CircularProgress, Portal, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../config';

const AssistantChat = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('story');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 420, height: 520 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ mouseX: 0, mouseY: 0, width: 420, height: 520 });

  const handleToggleMode = (_e, newMode) => {
    if (newMode) setMode(newMode);
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || loading) return;

    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const resp = await fetch(`${API_URL}/api/ai/assistant-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ messages: nextMessages, mode })
      });

      if (!resp.ok) {
        throw new Error('Assistant request failed');
      }

      const data = await resp.json();
      if (data?.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      }
    } catch (err) {
      console.error('Assistant chat error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong talking to the assistant.' }]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize default position near bottom-right when first opened
  useEffect(() => {
    if (open && (position.x === null || position.y === null)) {
      const width = size.width;
      const height = size.height;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;

      const defaultX = Math.max(16, viewportWidth - width - 24);
      const defaultY = Math.max(72, viewportHeight - height - 24);

      setPosition({ x: defaultX, y: defaultY });
    }
  }, [open, position.x, position.y, size.width, size.height]);

  // Drag handlers for the header area
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event) => {
      setPosition(prev => {
        const newX = event.clientX - dragOffsetRef.current.x;
        const newY = event.clientY - dragOffsetRef.current.y;
        return {
          x: Math.max(8, newX),
          y: Math.max(40, newY)
        };
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleDragStart = (event) => {
    // Only start drag with primary mouse button
    if (event.button !== 0) return;
    if (position.x === null || position.y === null) return;

    dragOffsetRef.current = {
      x: event.clientX - position.x,
      y: event.clientY - position.y
    };
    setIsDragging(true);
  };

  // Resize handlers (bottom-right corner)
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event) => {
      setSize(prev => {
        const deltaX = event.clientX - resizeStartRef.current.mouseX;
        const deltaY = event.clientY - resizeStartRef.current.mouseY;

        const newWidth = Math.max(320, resizeStartRef.current.width + deltaX);
        const newHeight = Math.max(360, resizeStartRef.current.height + deltaY);

        return {
          width: newWidth,
          height: newHeight
        };
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (event) => {
    if (event.button !== 0) return;
    setIsResizing(true);
    resizeStartRef.current = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      width: size.width,
      height: size.height
    };
  };

  return (
    <>
      {/* Floating launcher button */}
      <Tooltip title={open ? 'Hide the assistant' : 'Open the Luminara assistant for story or product help'}>
        <span>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              color: 'text.secondary',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            {open ? <CloseIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      {open && (
        <Portal>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              top: position.y ?? 80,
              left: position.x ?? 24,
              width: size.width,
              height: size.height,
              display: 'flex',
              flexDirection: 'column',
              zIndex: 9999,
              p: 2
            }}
          >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'move',
              userSelect: isDragging ? 'none' : 'auto'
            }}
            onMouseDown={handleDragStart}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Luminara Assistant
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={mode}
              exclusive
              onChange={handleToggleMode}
            >
              <Tooltip title="Brainstorm ideas, loglines, characters, and beats">
                <span>
                  <ToggleButton value="story">
                    Story
                  </ToggleButton>
                </span>
              </Tooltip>
              <Tooltip title="Ask how to use any feature inside Luminara">
                <span>
                  <ToggleButton value="help">
                    Help
                  </ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              mb: 1,
              pr: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 1
            }}
          >
            {messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Ask for story ideas, loglines, character help, or how to use parts of the app.
              </Typography>
            ) : (
              messages.map((m, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 1,
                    textAlign: m.role === 'user' ? 'right' : 'left'
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: m.role === 'user' ? 'primary.main' : 'background.default',
                      color: m.role === 'user' ? 'primary.contrastText' : 'text.primary'
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={mode === 'help' ? 'Ask how to use Luminara...' : 'Ask for story help...'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Tooltip title={input.trim() ? 'Send your prompt to the assistant' : 'Type a question or prompt first'}>
              <span>
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          </Box>
          {/* Resize handle (bottom-right corner) */}
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              position: 'absolute',
              right: 4,
              bottom: 4,
              width: 16,
              height: 16,
              cursor: 'se-resize',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              boxShadow: 1
            }}
          />
          </Paper>
        </Portal>
      )}
    </>
  );
};

export default AssistantChat;
